import {
  sortedAutoRtRows,
  hasForceDisplayFormat,
  matchesRtRowCondition,
  matchesAutoRtExtraGate,
  resolveRequestableMode,
  isAutoRtRequestable,
  buildAutoRtDialogInfo
} from '@/plugins/rt-dialog-migration'
import { getAutoRtSession } from '@/composables/useAutoRtSession'

type RtCondition = {
  client_id: string
  permission_type: number
  request_able: boolean
  check_flag?: unknown
}

type UnknownRecord = Record<string, any>

const isShokumukeirekiFormat = (formatId: string): boolean => formatId.includes('shokumukeirekisho')

type DownloadItemVm = {
  $firebase: { user: { uid: string; email?: string } }
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $axios: { post: (url: string, data: FormData, config: { timeout: number }) => Promise<any> }
  $varuna: { endPoints: { displayDialog: string } }
  $globalVar: UnknownRecord
}

export function useDownloadItemAutoRt (vm: DownloadItemVm) {
  const autoRt = getAutoRtSession()

  const notifyDisplay = (formatId: string, clientId: string, permissionType: number) => {
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatId)
    formData.append('dialog_name', 'auto_rt')
    formData.append('client_id', clientId)
    formData.append('permission_type', String(permissionType))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('display_count_num', '1')
    vm.$axios.post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 }).catch((error: unknown) => {
      console.error('[useDownloadItemAutoRt] notifyDisplay failed', {
        formatId,
        clientId,
        permissionType,
        error
      })
    })
  }

  const selectAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord): UnknownRecord | null => {
    // ヤマト住建（is_BehindRT）向けには auto_rt を一切表示しない。
    if (vm.$globalVar.is_BehindRT && vm.$globalVar.is_BehindRT()) {
      return null
    }

    // shokumukeirekisho フォーマット時は keyword/grade/foreignStudent の判定対象を otherFormatData に切替える。
    const isShoku = isShokumukeirekiFormat(formatId)
    const conditionFormatData: UnknownRecord = isShoku ? otherFormatData : formatData
    const isMobileDevice = vm.$deviceLib.isMobileDevice === true

    const conditionCtx = {
      formatId,
      formatData: conditionFormatData,
      basicData,
      isMobileDevice,
      addressCheck: (bd: unknown, prefectures: string[]) => vm.$globalVar.addressCheck(bd, prefectures),
      keywordCheckRirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckRirekisho(fd, keywords),
      keywordCheckShokumukeirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckShokumukeirekisho(fd, keywords)
    }
    const gateCtx = {
      basicData,
      formatData: conditionFormatData,
      isMobileDevice,
      userEmail: (vm.$firebase && vm.$firebase.user && vm.$firebase.user.email) || ''
    }

    const rows = sortedAutoRtRows(rtconditions)
    for (const row of rows) {
      const clientId = String(row.client_id || '')
      const spec = resolveRequestableMode(row)
      // SetAutoRT:
      // - 旧 permission 行が API レスポンスに無くても表示可（行欠落 = 未制限）
      // - check_flag !== '1' なら他 permission 送客済みでも新が requestable なら表示可
      if (!isAutoRtRequestable(rtconditions, clientId, spec, row.check_flag)) continue

      const conditionMatched = hasForceDisplayFormat(row, formatId) ||
        (matchesRtRowCondition(row, conditionCtx) && matchesAutoRtExtraGate(row, gateCtx))
      if (!conditionMatched) continue

      return buildAutoRtDialogInfo(row)
    }
    return null
  }

  const evaluateAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    // 単一の真実源: 評価ごとに状態をリセットし、選定結果から表示・状態を一括適用する
    autoRt.clearAutoRtInfo()

    const selected = selectAutoRT(rtconditions, basicData, formatId, formatData, otherFormatData)
    if (!selected) {
      return null
    }

    autoRt.setAutoRtInfo({ ...selected, agreed: true })
    return selected
  }

  return {
    evaluateAutoRT,
    selectAutoRT,
    notifyDisplay,
    setAgreed: autoRt.setAgreed,
    clearAutoRtInfo: autoRt.clearAutoRtInfo,
    getAutoRtInfo: autoRt.getAutoRtInfo,
    isActive: () => !!autoRt.getAutoRtInfo().clientId
  }
}
